import type { RuleSummary } from "@unbox/api";
import { useState } from "react";
import { SampleRulesDrawer } from "@/components/sample-requests/sample-rules-drawer";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { cx } from "@/utils/cx";
import { RuleSignalIcon } from "./rule-signal-icon";
import { RULE_TOOLTIP_CLASSNAME } from "./rule-source-tooltip";

type RulesMatchIndicatorProps = {
  rules: RuleSummary[];
  variant: "pill" | "inline";
};

const CollaborationMatchesContent = ({ count, onSeeRules }: { count: number; onSeeRules: () => void }) => {
  return (
    <p>
      {count} {count === 1 ? "rule matches" : "rules match"} pending sample requests in this collaboration.{" "}
      <button
        type="button"
        className="cursor-pointer font-semibold underline"
        onClick={(e) => {
          e.stopPropagation();
          onSeeRules();
        }}
      >
        See rules
      </button>
    </p>
  );
};

export const RulesMatchIndicator = ({ rules, variant }: RulesMatchIndicatorProps) => {
  const hasMatches = rules.length > 0;
  const [sampleRulesOpen, setSampleRulesOpen] = useState(false);

  const trigger =
    variant === "pill" ? (
      <span
        className={cx(
          "inline-flex h-[21px] items-center rounded-md border px-1.5",
          hasMatches
            ? "border-utility-pink-200 bg-utility-pink-50 text-utility-pink-700"
            : "border-utility-gray-200 bg-utility-gray-50 text-utility-gray-500",
        )}
      >
        <RuleSignalIcon className="size-3" />
      </span>
    ) : (
      <span
        className={cx(
          "inline-flex items-center gap-1 text-xs font-medium",
          hasMatches ? "text-utility-pink-700" : "text-utility-gray-500",
        )}
      >
        Rules
        <RuleSignalIcon />
        {rules.length}
      </span>
    );

  return (
    <>
      <HoverCard openDelay={100} closeDelay={150}>
        <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
        <HoverCardContent side="top" className={RULE_TOOLTIP_CLASSNAME}>
          {hasMatches ? (
            <CollaborationMatchesContent count={rules.length} onSeeRules={() => setSampleRulesOpen(true)} />
          ) : (
            <p>No rules matched</p>
          )}
        </HoverCardContent>
      </HoverCard>
      {sampleRulesOpen && <SampleRulesDrawer open onOpenChange={setSampleRulesOpen} />}
    </>
  );
};
